/**
 * Header — Product wordmark + module navigation tabs.
 */
import { useState } from 'react'
import type { ModuleId } from '@/types'
import { MODULES } from '@/types'
import TenantAdminModal from '@/components/admin/TenantAdminModal'

interface HeaderProps {
  activeModule?: ModuleId
  onModuleChange: (id: ModuleId | null) => void
}

export default function Header({ activeModule, onModuleChange }: HeaderProps) {
  const [adminOpen, setAdminOpen] = useState(false)

  return (
    <>
      <header className="sticky top-0 z-40 glass-bar border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-5 h-14 flex items-center gap-6">

          {/* Wordmark — click to go home */}
          <button
            onClick={() => onModuleChange(null)}
            className="flex items-center gap-2.5 select-none flex-shrink-0
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded-md p-1 -m-1"
            aria-label="Go to home"
          >
            <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-navy-900">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M7 1.5 L12.5 12.5 H1.5 Z" fill="none" stroke="#1D6FEB" strokeWidth="1.5" strokeLinejoin="round"/>
                <line x1="3.5" y1="9" x2="10.5" y2="9" stroke="#1D6FEB" strokeWidth="1.2"/>
              </svg>
            </span>
            <span className="font-semibold text-sm tracking-tight text-slate-900">Analyst</span>
            <span className="hidden sm:inline text-xs text-slate-400 font-normal">by Ainnov8</span>
          </button>

          {/* Divider */}
          <span className="hidden sm:block w-px h-5 bg-slate-200 flex-shrink-0" aria-hidden="true" />

          {/* Module tabs */}
          <nav className="flex items-center gap-1 overflow-x-auto flex-1 min-w-0" aria-label="Modules">
            {MODULES.map(m => {
              const isActive = activeModule === m.id
              return (
                <button
                  key={m.id}
                  onClick={() => onModuleChange(m.id)}
                  aria-current={isActive ? 'page' : undefined}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                    transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand
                    ${isActive
                      ? 'bg-navy-900 text-white shadow-sm'
                      : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                    }`}
                >
                  <span aria-hidden="true">{m.icon}</span>
                  <span className="hidden md:inline">{m.label}</span>
                  <span className="md:hidden">{m.label.split(' ')[0]}</span>
                </button>
              )
            })}
          </nav>

          {/* Discrete admin icon — far right */}
          <button
            onClick={() => setAdminOpen(true)}
            className="flex-shrink-0 p-1.5 rounded-md text-slate-300 hover:text-slate-500
                       hover:bg-slate-100 transition-colors
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            aria-label="Admin settings"
            title="Admin settings"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M8 10.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5ZM8 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm5.657-1.5-.006-.07-.005-.051 1.172-.853a.5.5 0 0 0 .143-.641l-1-1.732a.5.5 0 0 0-.625-.196l-1.347.538a4.475 4.475 0 0 0-.86-.497L10.996 3.5A.5.5 0 0 0 10.5 3h-2a.5.5 0 0 0-.496.43l-.133 1.497a4.476 4.476 0 0 0-.861.497L5.664 4.886a.5.5 0 0 0-.625.196l-1 1.732a.5.5 0 0 0 .143.641l1.172.853A4.553 4.553 0 0 0 5.343 8l.01.07.006.051-1.172.853a.5.5 0 0 0-.143.641l1 1.732a.5.5 0 0 0 .625.196l1.347-.538c.271.185.557.346.861.497l.133 1.498A.5.5 0 0 0 8.5 13h2a.5.5 0 0 0 .496-.43l.133-1.497a4.476 4.476 0 0 0 .861-.497l1.346.538a.5.5 0 0 0 .625-.196l1-1.732a.5.5 0 0 0-.143-.641l-1.172-.853Z"/>
            </svg>
          </button>
        </div>
      </header>

      {adminOpen && <TenantAdminModal onClose={() => setAdminOpen(false)} />}
    </>
  )
}
